/**
 * D-EZ-110 — THE SEASON'S ARC: chapter markers through the year.
 *
 * A season has a shape — how it began, where it turned, how it ended — and the
 * rivalries that ran through it. Four markers fall on races the calendar
 * already makes special (D-012): the opener (race 1), halfway (race 13), the
 * cut to the playoffs (race 26), the final four set (race 35) — and the finale
 * (race 36). Each is read from the season as it stood after that race, and the
 * whole arc feeds the year in review (`story/yearInReview.ts`).
 *
 * ⚠ NO LOOK-AHEAD. `seasonArc(seasons, n, throughRace)` reads races up to and
 * including `throughRace` and nothing after: the standings of each marker are
 * that race's own fold (`standingsAfter`), never the final table. "The turn"
 * is a judgement about the whole year, so it is only written once the year is
 * over (proved in `chapters.lookahead.test.ts`).
 */

import { REGULAR_SEASON_RACES, SEASON_RACES } from '../schedule.ts';
import type { PlayoffRoundResult, PlayoffSeed, SeasonState } from '../seasonPlay.ts';
import { liveHistorySeason } from '../history/index.ts';
import type { SeasonStandings } from '../standings.ts';
import { asHistory, chapterContext, seasonsThrough, standingsAfter, type ChapterSeason } from './league.ts';
import { rookieRace } from './rookies.ts';
import { listOf } from './farewell.ts';
import { count, ordinal, plural, surname, tell, tellOne, type Told } from './text.ts';

export type ChapterMark = 'opener' | 'halfway' | 'cut' | 'final-four' | 'finale';

/** The race each chapter closes on. */
export const CHAPTER_RACES: Readonly<Record<ChapterMark, number>> = {
  opener: 1,
  halfway: 13,
  cut: REGULAR_SEASON_RACES,
  'final-four': SEASON_RACES - 1,
  finale: SEASON_RACES,
};

const TITLES: Record<ChapterMark, string> = {
  opener: 'The opener',
  halfway: 'Halfway',
  cut: 'The cut',
  'final-four': 'The final four',
  finale: 'The finale',
};

export interface ArcChapter {
  mark: ChapterMark;
  raceNumber: number;
  title: string;
  lines: Told[];
}

export interface SeasonArc {
  seasonNumber: number;
  throughRace: number;
  chapters: ArcChapter[];
  /** For the year in review — written only once the season is over. */
  began: Told | null;
  turn: Told | null;
  ended: Told | null;
  /** The rivalries that defined it, hottest first. */
  rivalries: Told[];
}

interface Bracket {
  seeds: readonly PlayoffSeed[];
  rounds: readonly PlayoffRoundResult[];
}

/** A season being raced, as a chapter reads it (with its bracket). */
export function liveChapterSeason(live: {
  seasonNumber: number;
  roster: ChapterSeason['roster'];
  teams: ChapterSeason['teams'];
  state: SeasonState;
}): ChapterSeason {
  const h = liveHistorySeason(live);
  return {
    ...h,
    season: {
      ...h.season,
      playoff: { seeds: live.state.playoff.seeds, rounds: live.state.playoff.rounds, finalFour: [], championId: null, championRacePoints: null },
    },
  } as unknown as ChapterSeason;
}

function bracketOf(s: ChapterSeason, through: number): Bracket {
  const p = s.season.playoff;
  if (!p) return { seeds: [], rounds: [] };
  return {
    seeds: through >= REGULAR_SEASON_RACES ? p.seeds : [],
    rounds: p.rounds.filter((r) => r.races.every((n) => n <= through)),
  };
}

/** The arc of season `seasonNumber` through race `throughRace` (default: all of it). */
export function seasonArc(input: readonly ChapterSeason[], seasonNumber: number, throughRace?: number): SeasonArc | null {
  const all = seasonsThrough(input, seasonNumber, { winter: false });
  const full = all.find((x) => x.seasonNumber === seasonNumber);
  if (!full) return null;
  const run = full.season.races.map((r) => r.raceNumber);
  const through = Math.min(throughRace ?? Math.max(0, ...run), Math.max(0, ...run));
  if (through < 1) return null;
  const races = full.season.races.filter((r) => r.raceNumber <= through);
  const settled = full.complete !== false && through === SEASON_RACES && races.length === full.season.races.length;
  const s: ChapterSeason = {
    ...full,
    season: { ...full.season, races, championId: settled ? full.season.championId : null },
    complete: settled,
  };
  const before = all.filter((x) => x.seasonNumber < seasonNumber);
  const cx = chapterContext([...before, s], input);
  const K = (...p: (string | number)[]) => `arc|${cx.leagueKey}|${seasonNumber}|${p.join('|')}`;
  const name = (id: string) => cx.registry.byId[id]?.name ?? id;
  const prevChamp = before.find((x) => x.seasonNumber === seasonNumber - 1)?.season.championId ?? null;
  const winsBefore = (id: string) => before.reduce((t, x) => t + (x.season.finalStandings.byDriver[id]?.wins ?? 0), 0) + (cx.registry.byId[id]?.prior?.wins ?? 0);
  const bracket = bracketOf(s, through);
  const chapters: ArcChapter[] = [];
  const seasonMoments = (upTo: number) => {
    const pairs: { a: string; b: string; heat: number; worst: string | null }[] = [];
    for (const r of cx.people.book.byPair.values()) {
      const ms = r.moments.filter((m) => m.seasonNumber === seasonNumber && m.raceNumber <= upTo && m.raceNumber > 0);
      if (ms.length === 0) continue;
      const heat = ms.reduce((t, m) => t + m.heat, 0);
      const worst = [...ms].sort((x, y) => y.heat - x.heat || x.raceNumber - y.raceNumber)[0];
      pairs.push({ a: r.aName, b: r.bName, heat, worst: worst?.text ?? null });
    }
    return pairs.sort((x, y) => y.heat - x.heat || x.a.localeCompare(y.a));
  };

  // ---- the opener -------------------------------------------------------------
  const r1 = races.find((r) => r.raceNumber === 1);
  if (r1) {
    const lines: Told[] = [];
    const w = r1.race.winnerId;
    if (w) {
      // In the league's first season nobody has a record to be first against.
      const first = before.length > 0 && winsBefore(w) === 0;
      lines.push(
        first
          ? tellOne(K('op'), ['{name} opened season {n} with the first win of his career, in the {race}.', 'Season {n} began with a first-time winner: {name}, in the {race}.'], { name: name(w), n: seasonNumber, race: r1.scheduled.raceName })
          : tellOne(K('op'), ['{name} won the {race} to open season {n}.', 'Season {n} opened at {track}, and {name} won it.', 'First race, first trophy: {name} in the {race}.'], { name: name(w), n: seasonNumber, race: r1.scheduled.raceName, track: r1.scheduled.trackName }),
      );
    }
    if (prevChamp && prevChamp !== w) {
      const f = r1.race.finishingOrder.find((x) => x.driverId === prevChamp);
      if (f) lines.push(tell('The defending champion, {name}, finished {pos}.', { name: name(prevChamp), pos: ordinal(f.place) }));
    }
    const m = (cx.people.book.momentsByRace.get(`${seasonNumber}|1`) ?? []).slice().sort((a, b) => b.heat - a.heat)[0];
    if (m && m.heat >= 4) lines.push(tell('{text}', { text: m.text }));
    chapters.push({ mark: 'opener', raceNumber: 1, title: TITLES.opener, lines });
  }

  // ---- halfway ------------------------------------------------------------------
  const pointsChapter = (mark: ChapterMark, n: number, lead: string[]) => {
    const t = standingsAfter(s, n);
    if (!t || t.drivers.length < 2) return null;
    const byPoints = [...t.drivers].sort((a, b) => b.points - a.points || a.rank - b.rank);
    const [a, b] = byPoints;
    const lines: Told[] = [];
    lines.push(tellOne(K(mark, 'lead'), lead, { n: count(n), leader: a.name, gap: a.points - b.points, pl: plural(a.points - b.points, 'point'), second: b.name }));
    const raced = races.filter((r) => r.raceNumber <= n);
    const winners = new Map<string, number>();
    for (const r of raced) if (r.race.winnerId) winners.set(r.race.winnerId, (winners.get(r.race.winnerId) ?? 0) + 1);
    const most = [...winners.entries()].sort((x, y) => y[1] - x[1] || x[0].localeCompare(y[0]))[0];
    if (most) lines.push(tell('{k} different winners so far; {name} has the most, with {w}.', { k: count(winners.size), name: name(most[0]), w: count(most[1]) }));
    return { t, lines };
  };
  if (through >= CHAPTER_RACES.halfway) {
    const c = pointsChapter('halfway', CHAPTER_RACES.halfway, [
      '{n} races in, {leader} leads the points by {gap} {pl} over {second}.',
      'Halfway to the cut: {leader} on top, {gap} {pl} clear of {second}.',
      'At the halfway mark of the regular season it is {leader} from {second}, {gap} {pl} apart.',
    ]);
    if (c) {
      const rr = rookieRace(input.map((x) => (x.seasonNumber === seasonNumber ? s : x)), seasonNumber, CHAPTER_RACES.halfway);
      if (rr?.line) c.lines.push(rr.line);
      const hot = seasonMoments(CHAPTER_RACES.halfway)[0];
      if (hot && hot.heat >= 11) c.lines.push(hot.worst ? tell('The feud of the first half: {a} and {b}. {worst}', { a: hot.a, b: hot.b, worst: hot.worst }) : tell('The feud of the first half: {a} and {b}.', { a: hot.a, b: hot.b }));
      chapters.push({ mark: 'halfway', raceNumber: CHAPTER_RACES.halfway, title: TITLES.halfway, lines: c.lines });
    }
  }

  // ---- the cut -------------------------------------------------------------------
  if (through >= CHAPTER_RACES.cut) {
    const c = pointsChapter('cut', CHAPTER_RACES.cut, [
      'The regular season is done: {leader} finished it on top, {gap} {pl} ahead of {second}.',
      '{leader} won the regular season by {gap} {pl} from {second}.',
    ]);
    if (c) {
      const seeds = bracket.seeds;
      if (seeds.length > 0) {
        const viaWin = seeds.filter((x) => x.viaWin).length;
        c.lines.push(viaWin === seeds.length ? tell('{k} drivers made the playoff, every one of them on the strength of a win.', { k: count(seeds.length) }) : tell('{k} drivers made the playoff, {w} of them on the strength of a win.', { k: count(seeds.length), w: count(viaWin) }));
        const seeded = new Set(seeds.map((x) => x.driverId));
        const out = [...c.t.drivers].sort((a, b) => b.points - a.points).find((d) => !seeded.has(d.driverId));
        const lastIn = [...seeds].filter((x) => !x.viaWin).sort((a, b) => a.points - b.points)[0];
        if (out && lastIn && lastIn.points >= out.points) {
          const gap = lastIn.points - out.points;
          c.lines.push(gap === 0 ? tell('{name} missed the cut on a tiebreaker.', { name: out.name }) : tell('{name} was the first man out, {gap} {pl} short.', { name: out.name, gap, pl: plural(gap, 'point') }));
        }
        if (prevChamp && !seeded.has(prevChamp) && c.t.byDriver[prevChamp]) c.lines.push(tell('The defending champion, {name}, did not make it.', { name: name(prevChamp) }));
      }
      chapters.push({ mark: 'cut', raceNumber: CHAPTER_RACES.cut, title: TITLES.cut, lines: c.lines });
    }
  }

  // ---- the final four --------------------------------------------------------------
  if (through >= CHAPTER_RACES['final-four']) {
    const r8 = bracket.rounds.find((r) => r.round === 'round-of-8');
    const last = races.find((r) => r.raceNumber === SEASON_RACES) ?? null;
    if (r8 && r8.advanced.length > 0) {
      const lines: Told[] = [tell('{list} go to the last race for the title.', { list: listOf(r8.advanced.map(name)) })];
      const topSeed = bracket.seeds[0];
      const outTop = topSeed && bracket.rounds.some((r) => r.eliminated.includes(topSeed.driverId));
      if (outTop) {
        const where = bracket.rounds.find((r) => r.eliminated.includes(topSeed.driverId));
        lines.push(tell('The top seed, {name}, went out in the {round}.', { name: topSeed.name, round: where?.label ?? 'playoff' }));
      }
      void last;
      chapters.push({ mark: 'final-four', raceNumber: CHAPTER_RACES['final-four'], title: TITLES['final-four'], lines });
    }
  }

  // ---- the finale and the shape of the year ----------------------------------------
  let began: Told | null = null;
  let turn: Told | null = null;
  let ended: Told | null = null;
  const rivalries: Told[] = [];
  if (settled) {
    const champ = full.season.championId;
    const last = races[races.length - 1];
    const lines: Told[] = [];
    if (champ && last) {
      const f = last.race.finishingOrder.find((x) => x.driverId === champ);
      const titles = (cx.registry.byId[champ]?.bySeason ?? []).filter((l) => l.champion).length;
      const slots = { name: name(champ), race: last.scheduled.raceName, pos: f ? ordinal(f.place) : '', nth: ordinal(titles) };
      ended = f?.place === 1
        ? tellOne(K('end'), ['{name} won the {race} and the championship with it.', 'It ended the only way {name} wanted: a win in the {race}, and the title.'], slots)
        : f
          ? tellOne(K('end'), ['{name} brought it home {pos} in the {race}, and that was enough for the title.', 'The title came to {name} with a {pos} in the {race}.'], slots)
          : tell('{name} is the champion.', slots);
      lines.push(ended);
      if (titles > 1) lines.push(tell('That makes {k} titles for him.', { k: count(titles) }));
    }
    chapters.push({ mark: 'finale', raceNumber: SEASON_RACES, title: TITLES.finale, lines });
    began = chapters.find((c) => c.mark === 'opener')?.lines[0] ?? null;
    turn = theTurn(s, bracket, name, K);
    for (const p of seasonMoments(SEASON_RACES).slice(0, 3)) {
      if (p.heat < 11) break;
      rivalries.push(p.worst ? tell('{a} and {b}: {worst}', { a: p.a, b: p.b, worst: p.worst }) : tell('{a} and {b}.', { a: p.a, b: p.b }));
    }
  }
  void asHistory;
  void surname;
  return { seasonNumber, throughRace: through, chapters, began, turn, ended, rivalries };
}

/**
 * Where the year turned. If the regular-season leader was knocked out of the
 * playoff, that is the turn; otherwise it is the last time the points lead
 * changed hands in the regular season (or, if nobody ever took it off him,
 * the race he took it).
 */
function theTurn(s: ChapterSeason, bracket: Bracket, name: (id: string) => string, K: (...p: (string | number)[]) => string): Told | null {
  const leaderAfter = (n: number): string | null => {
    const t: SeasonStandings | null = standingsAfter(s, n);
    if (!t || t.drivers.length === 0) return null;
    return [...t.drivers].sort((a, b) => b.points - a.points || a.rank - b.rank)[0].driverId;
  };
  const rsLeader = leaderAfter(REGULAR_SEASON_RACES);
  const champ = s.season.championId;
  if (rsLeader && rsLeader !== champ) {
    const where = bracket.rounds.find((r) => r.eliminated.includes(rsLeader));
    if (where) {
      return tellOne(K('turn-out'), ['The year turned in the {round}: {name}, the regular-season leader, was knocked out.', '{name} led the regular season and went out in the {round}. That was the turn.'], { round: where.label, name: name(rsLeader) });
    }
  }
  let lastChange = 0;
  let prev: string | null = null;
  for (let n = 1; n <= REGULAR_SEASON_RACES; n++) {
    const l = leaderAfter(n);
    if (l === null) continue;
    if (l !== prev) lastChange = n;
    prev = l;
  }
  if (lastChange === 0 || !rsLeader) return null;
  const race = s.season.races.find((r) => r.raceNumber === lastChange);
  if (lastChange === 1) return tell('{name} took the points lead in the opener and never gave it up.', { name: name(rsLeader) });
  return tellOne(K('turn'), ['The turn came in race {n}, the {race}: {name} took the points lead there and kept it to the cut.', '{name} went to the top of the points in the {race} (race {n}) and nobody moved him.'], { n: lastChange, race: race?.scheduled.raceName ?? `race ${lastChange}`, name: name(rsLeader) });
}

/** The chapter that closes on race `raceNumber`, if one does. */
export function chapterAt(input: readonly ChapterSeason[], seasonNumber: number, raceNumber: number): ArcChapter | null {
  const mark = (Object.keys(CHAPTER_RACES) as ChapterMark[]).find((m) => CHAPTER_RACES[m] === raceNumber);
  if (!mark) return null;
  const arc = seasonArc(input, seasonNumber, raceNumber);
  return arc?.chapters.find((c) => c.mark === mark) ?? null;
}
